import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from "react";

import { useClippy } from "./ClippyContext";
import { Modal, Frame, TitleBar, useModal, ModalEvents } from "@react95/core";
import { useLocalNumber, useLocalString } from "../hooks/useLocalStorage";
import { getWebSocketUrl } from "../config/websocket";
import { SocketManager, SocketMessage, getClientFingerprint } from "../lib/SocketManager";
import MailProvider from "./MailContext";
import SecretProvider from "./SecretContext";
import ChatProvider from "./ChatContext";
import CTFProvider from "./CTFContext";
import WebProvider from "./WebContext";
import { WINDOW_NAMES } from "../config/const";

/**
 * App status type
 */
export type AppStatus = "idle" | "connecting" | "authenticating" | "ready" | "reconnecting" | "error";

/**
 * Message listener callback type
 */
export type MessageListener = (message: any) => void;

type ModelDef = {
  id: string;
  title: string;
  icon?: React.ReactElement;
  hasButton: boolean;
};

type ModalData = {
  [key: string]: any;
};

export interface AppContextType {
  appEnableDebug: boolean;

  openModal: (modalDef: ModelDef, data?: ModalData, target?: string) => void;
  closeModal: (id: string) => void;
  getModalData: (id: string) => ModalData | undefined;

  // internet client instance tracking
  getInternetClientInstances: () => Map<string, ModalData | undefined>;

  windowSmall: boolean;

  // volume control
  volume: number;
  setVolume: (volume: number) => void;

  // session management
  sessionId: string | null;
  sessionPassword: string | null;
  sessionReady: boolean;
  isSocketReady: boolean;
  lastAuthTime: number;

  // theme management
  theme: string;
  setTheme: (theme: string) => void;

  // reset session (clear saved ids and request a fresh session)
  resetSession: () => void;

  // connection
  status: AppStatus;
  error: string | null;

  // websocket methods
  sendMessage: (service: string, action: string, payload: Record<string, any>) => void;
  connect: () => void;
  disconnect: () => void;
  clearError: () => void;

  // message listener
  onMessage: (action: string, callback: MessageListener) => () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}

export function useMessageListener(action: string, callback: MessageListener) {
  const { onMessage } = useApp();

  useEffect(() => {
    return onMessage(action, callback);
  }, [action, callback, onMessage]);
}

export function useWindowSize() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useWindowSize must be used within an AppProvider");
  }
  return context.windowSmall;
}

export default function AppProvider({ children }: { children: ReactNode }) {
  //// DEBUG MODE

  // use  effect listen for when user pressed a and shift to toggle debug mode
  const [appEnableDebug, setAppEnableDebug] = useState<boolean>(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "D" && e.shiftKey) {
        setAppEnableDebug((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  //// MODAL MANAGEMENT

  const { add, minimize, restore, focus, remove, subscribe } = useModal();
  const [activeModals, setActiveModals] = useState<Set<string>>(new Set());
  const [modalData, setModalDataState] = useState<Record<string, ModalData>>({});
  const modalDataRef = useRef<Record<string, ModalData>>(modalData);
  // keep ref in sync with state so `getModalData` can be stable
  useEffect(() => {
    modalDataRef.current = modalData;
  }, [modalData]);

  const [internetClientInstances, setInternetClientInstances] = useState<Map<string, ModalData | undefined>>(new Map());
  const hasNotifiedNoModalsRef = useRef(false);

  const openModal = (modalDef: ModelDef, data?: ModalData, target?: string) => {
    // for internet client, generate unique id if target not provided
    let modalId = modalDef.id;
    if (modalDef.id === WINDOW_NAMES.InternetClient && !target) {
      target = `browser-${Math.random().toString(36).substr(2, 9)}`;
      modalId = target;
    } else if (modalDef.id === WINDOW_NAMES.InternetClient && target) {
      modalId = target;
    }

    console.log("[AppContext] openModal called with id:", modalId, "data:", data);
    
    // if target modal already exists, just focus it and update data
    if (target && activeModals.has(target)) {
      console.log("[AppContext] target modal already exists, focusing:", target);
      if (data) {
        setModalDataState((prev) => {
          const newState = { ...prev, [target!]: data };
          return newState;
        });
      }
      focus(target);
      return;
    }

    if (data) {
      setModalDataState((prev) => {
        const newState = { ...prev, [modalId]: data };
        console.log("[AppContext] setModalDataState, newState:", newState);
        return newState;
      });
    }

    // track internet client instances
    if (modalDef.id === WINDOW_NAMES.InternetClient && target) {
      setInternetClientInstances((prev) => new Map([...prev, [target, data]]));
    }

    add({ ...modalDef, id: modalId });

    restore(modalId);
    focus(modalId);
  };

  const getModalData = useCallback((id: string) => {
    const data = modalDataRef.current[id];
    console.log("[AppContext] getModalData called for id:", id, "result:", data);
    return data;
  }, []);

  const getInternetClientInstances = () => {
    return internetClientInstances;
  };

  const closeModal = (id: string) => {
    minimize(id);
    remove(id);
    setModalDataState((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    // clean up internet client instances
    setInternetClientInstances((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  // listen for modal state changes and notify server when no modals are active
  useEffect(() => {
    const handleAddModal = (data: any) => {
      setActiveModals((prev) => new Set([...prev, data.id]));
      hasNotifiedNoModalsRef.current = false;
    };

    const handleRemoveModal = (data: any) => {
      setActiveModals((prev) => {
        const next = new Set(prev);
        next.delete(data.id);
        return next;
      });
    };

    const handleMinimizeModal = (data: any) => {
      setActiveModals((prev) => {
        const next = new Set(prev);
        next.delete(data.id);
        return next;
      });
    };

    const handleRestoreModal = (data: any) => {
      setActiveModals((prev) => new Set([...prev, data.id]));
      hasNotifiedNoModalsRef.current = false;
    };

    const unsubAdd = subscribe(ModalEvents.AddModal, handleAddModal);
    const unsubRemove = subscribe(ModalEvents.RemoveModal, handleRemoveModal);
    const unsubMinimize = subscribe(ModalEvents.MinimizeModal, handleMinimizeModal);
    const unsubRestore = subscribe(ModalEvents.RestoreModal, handleRestoreModal);

    return () => {
      unsubAdd();
      unsubRemove();
      unsubMinimize();
      unsubRestore();
    };
  }, [subscribe]);

  //// WEBSOCKET

  const socketRef = useRef<SocketManager | null>(null);
  const [status, setStatus] = useState<AppStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  //// VOLUME

  // const [volume, _setVolume] = useState<number>(0);
  const [volume, _setVolume] = useLocalNumber("app-volume", 0);
  const setVolume = (v: number) => {
    const vol = Math.max(0, Math.min(100, Math.round(v)));
    _setVolume(vol);
  };

  // session management state
  const [sessionId, setSessionId] = useLocalString("sessionId", null);
  const [sessionPassword, setSessionPassword] = useLocalString("sessionPassword", null);
  const [sessionReady, setSessionReady] = useState(false);
  const [lastAuthTime, setLastAuthTime] = useState<number>(0);
  const [isSocketReady, setIsSocketReady] = useState(false);
  
  // refs to track current session values (for use in callbacks that need fresh values)
  const sessionIdRef = useRef(sessionId);
  const sessionPasswordRef = useRef(sessionPassword);
  
  // keep refs in sync with state
  useEffect(() => {
    sessionIdRef.current = sessionId;
    sessionPasswordRef.current = sessionPassword;
  }, [sessionId, sessionPassword]);

  // Window size state
  const windowHeight = window.innerHeight;
  const [windowSmall] = useState(() => {
    return windowHeight <= 680;
  });

  // theme persisted in localstorage
  const [theme, _setTheme] = useLocalString("app-theme", "storm");
  const setTheme = (t: string) => {
    _setTheme(t);
  };

  // dynamically load the selected theme css using an explicit map to satisfy bundlers
  // and track injected stylesheet nodes so we can unload previous themes when switching.
  const injectedThemeNodesRef = useRef<Map<string, Node[]>>(new Map());
  const initialHeadSnapshotRef = useRef<Node[] | null>(null);
  const currentThemeRef = useRef<string | null>(null);

  // capture initial head and identify win95 nodes specifically so other themes are not
  // accidentally treated as the base. we look for link/style nodes that reference `win95.css`.
  useEffect(() => {
    if (initialHeadSnapshotRef.current === null) {
      const headChildren = Array.from(document.head.children);
      initialHeadSnapshotRef.current = headChildren;

      const win95Nodes = headChildren.filter((n) => {
        try {
          // check if element has an href containing win95.css (common for link tags)
          const href = (n as HTMLLinkElement).href;
          if (href && href.includes("win95.css")) return true;

          // some bundlers may inject style tags containing the theme name - best-effort check
          const txt = (n as HTMLStyleElement).textContent;
          if (txt && txt.includes("win95")) return true;
        } catch (e) {
          // ignore
        }
        return false;
      });

      // store found nodes for win95 (if not found, store empty array so we can still manage others)
      injectedThemeNodesRef.current.set("win95", win95Nodes);

      // don't force currentTheme here; let the applyTheme effect handle applying saved theme
      currentThemeRef.current = null;
    }
  }, []);

  // apply theme changes by disabling/enabling previously injected nodes (instead of removing)
  useEffect(() => {
    if (!theme) return;

    const applyTheme = async (t: string) => {
      const prev = currentThemeRef.current;

      // nothing to do if already active
      if (prev === t) return;

      // disable previous theme nodes (but never disable the static win95 nodes)
      if (prev && prev !== "win95") {
        const prevNodes = injectedThemeNodesRef.current.get(prev);
        if (prevNodes) {
          prevNodes.forEach((n) => {
            try {
              if ((n as HTMLLinkElement).media !== undefined) {
                (n as any).__original_media =
                  (n as any).__original_media ?? (n as HTMLLinkElement).media ?? "";
                (n as HTMLLinkElement).media = "not all";
              } else {
                (n as Element).setAttribute("data-theme-disabled", "1");
              }
            } catch (e) {
              // ignore
            }
          });
        }
      }

      // if selecting win95 make sure all other themes are disabled and set current
      if (t === "win95") {
        try {
          injectedThemeNodesRef.current.forEach((nodes, themeKey) => {
            if (themeKey === "win95") return; // keep base
            nodes.forEach((n) => {
              try {
                if ((n as HTMLLinkElement).media !== undefined) {
                  (n as any).__original_media =
                    (n as any).__original_media ?? (n as HTMLLinkElement).media ?? "";
                  (n as HTMLLinkElement).media = "not all";
                } else {
                  (n as Element).setAttribute("data-theme-disabled", "1");
                }
              } catch (e) {
                // ignore
              }
            });
          });
        } catch (e) {
          // ignore
        }

        currentThemeRef.current = "win95";
        _setTheme("win95");
        console.log("switched to win95 (base) and disabled other themes");
        return;
      }

      // if already loaded before, re-enable nodes
      const cached = injectedThemeNodesRef.current.get(t);
      if (cached) {
        cached.forEach((n) => {
          try {
            if ((n as HTMLLinkElement).media !== undefined) {
              (n as HTMLLinkElement).media = (n as any).__original_media ?? "";
            } else {
              (n as Element).removeAttribute("data-theme-disabled");
            }
          } catch (e) {
            // ignore
          }
        });

        currentThemeRef.current = t;
        _setTheme(t);
        console.log("re-enabled theme:", t);
        return;
      }

      // otherwise import and capture new nodes
      try {
        const beforeNodes = Array.from(document.head.children);

        switch (t) {
          case "millenium":
            await import("@react95/core/themes/millenium.css");
            break;
          case "storm":
            await import("@react95/core/themes/storm.css");
            break;
          case "marine":
            await import("@react95/core/themes/marine.css");
            break;
          case "spruce":
            await import("@react95/core/themes/spruce.css");
            break;
          case "plum":
            await import("@react95/core/themes/plum.css");
            break;
          case "rainyDay":
            await import("@react95/core/themes/rainyDay.css");
            break;
          case "slate":
            await import("@react95/core/themes/slate.css");
            break;
          case "travel":
            await import("@react95/core/themes/travel.css");
            break;
          case "maple":
            await import("@react95/core/themes/maple.css");
            break;
          case "rose":
            await import("@react95/core/themes/rose.css");
            break;
          default:
            console.warn("unknown theme requested, falling back to win95:", t);
            currentThemeRef.current = "win95";
            _setTheme("win95");
            return;
        }

        const afterNodes = Array.from(document.head.children);
        const newNodes = afterNodes.filter((n) => !beforeNodes.includes(n));

        newNodes.forEach((n) => {
          try {
            (n as any).__original_media = (n as any).media ?? "";
          } catch (e) {
            // ignore
          }
        });

        if (newNodes.length > 0) {
          injectedThemeNodesRef.current.set(t, newNodes);
        }

        currentThemeRef.current = t;
        _setTheme(t);
        console.log("loaded theme:", t);
      } catch (err) {
        console.error("failed to load theme", t, err);
        // remove partial nodes and fallback to win95
        try {
          const partial = injectedThemeNodesRef.current.get(t);
          if (partial) {
            partial.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
            injectedThemeNodesRef.current.delete(t);
          }
        } catch (e) {
          // ignore
        }

        currentThemeRef.current = "win95";
        _setTheme("win95");
        console.log("fallback to win95 after failed theme load");
      }
    };

    applyTheme(theme);
  }, [theme, _setTheme]);

  // Message listeners map: action -> array of callbacks
  const listenersRef = useRef<Map<string, MessageListener[]>>(new Map());
  // Track which actions are already subscribed to socket
  const socketSubscriptionsRef = useRef<Set<string>>(new Set());

  /**
   * Register a listener for a specific message action
   * Returns an unsubscribe function
   */
  const onMessage = useCallback((action: string, callback: MessageListener) => {
    if (!listenersRef.current.has(action)) {
      listenersRef.current.set(action, []);
    }
    listenersRef.current.get(action)!.push(callback);

    // subscribe to socket on first listener for this action
    // even if socket doesn't exist yet - connection will use pending subscriptions
    if (!socketSubscriptionsRef.current.has(action)) {
      socketSubscriptionsRef.current.add(action);
      if (socketRef.current) {
        socketRef.current.on(action, (msg) => {
          handleIncomingMessage(msg);
        });
      }
    }

    // return unsubscribe function
    return () => {
      const listeners = listenersRef.current.get(action);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }, []);

  /**
   * Dispatch a message to all registered listeners for that action
   */
  const dispatchMessage = useCallback((action: string, message: any) => {
    const listeners = listenersRef.current.get(action);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(message);
        } catch (err) {
          console.error(`Error in message listener for action "${action}":`, err);
        }
      });
    }
  }, []);

  const handleIncomingMessage = useCallback(
    (message: SocketMessage) => {
      // handle session messages
      if (message.service === "session") {
        if (message.action === "ready") {
          const sId = message.payload?.sessionId;
          const sPwd = message.payload?.password;

          console.log("✅ session authenticated, sessionId:", sId);

          if (sId) {
            setSessionId(sId);
            if (sPwd) {
              setSessionPassword(sPwd);
            }
            // signal all contexts: now safe to fetch data
            setLastAuthTime(Date.now());
            setSessionReady(true);
            setStatus("ready");
          }
          return;
        }

        // handle session errors - invalid or expired
        if (message.action === "error") {
          const errorMsg = message.payload?.message || "";
          if (
            errorMsg.includes("Invalid sessionId or password") ||
            errorMsg.includes("Session not authenticated")
          ) {
            console.warn("⚠️  session invalid, creating new session");
            setSessionReady(false);
            setSessionId(null);
            setSessionPassword(null);
            setStatus("error");

            // request new session with client fingerprint
            if (socketRef.current && socketRef.current.isReady()) {
              socketRef.current.send({
                service: "session",
                action: "new_session",
                payload: {
                  client: getClientFingerprint(),
                },
              });
            }
            return;
          }
        }
      }

      // dispatch to listeners
      dispatchMessage(message.action, message);
    },
    [dispatchMessage],
  );

  const connect = useCallback(async () => {
    try {
      // initialize socket manager if needed
      if (!socketRef.current) {
        const url = getWebSocketUrl();
        socketRef.current = new SocketManager(url);

        // set callback for when socket reconnects - fire AFTER socket open, BEFORE re-auth
        // use refs to get current session values instead of closure values
        socketRef.current.setOnReconnect(() => {
          console.log("[AppContext] 🔄 socket reconnected, re-authenticating...");
          setStatus("authenticating");

          if (socketRef.current && socketRef.current.isReady()) {
            if (sessionIdRef.current && sessionPasswordRef.current) {
              // resume existing session with client fingerprint
              socketRef.current.send({
                service: "session",
                action: "resume_session",
                payload: {
                  sessionId: sessionIdRef.current,
                  password: sessionPasswordRef.current,
                  client: getClientFingerprint(),
                },
              });
            } else {
              // create new session with client fingerprint
              socketRef.current.send({
                service: "session",
                action: "new_session",
                payload: {
                  client: getClientFingerprint(),
                },
              });
            }
          }
        });

        // subscribe to critical early actions (before any listeners register)
        socketRef.current.on("ready", handleIncomingMessage);
        socketRef.current.on("error", handleIncomingMessage);
        socketRef.current.on("greeting", handleIncomingMessage);
        socketSubscriptionsRef.current.add("ready");
        socketSubscriptionsRef.current.add("error");
        socketSubscriptionsRef.current.add("greeting");

        // subscribe to any pending actions that were registered before socket existed
        socketSubscriptionsRef.current.forEach((action) => {
          if (!["ready", "error", "greeting"].includes(action)) {
            socketRef.current!.on(action, (msg) => {
              handleIncomingMessage(msg);
            });
          }
        });
      }

      setStatus("connecting");
      setError(null);

      // attempt connection with timeout
      const connectPromise = socketRef.current.connect();
      const timeoutPromise = new Promise<void>((_, reject) =>
        setTimeout(() => reject(new Error("connection timeout")), 10000),
      );

      await Promise.race([connectPromise, timeoutPromise]);

      // socket is open, now authenticate
      setIsSocketReady(true);
      setStatus("authenticating");

      // request/resume session (use refs to get current values)
      if (socketRef.current && socketRef.current.isReady()) {
        if (sessionIdRef.current && sessionPasswordRef.current) {
          // resume existing session with client fingerprint
          socketRef.current.send({
            service: "session",
            action: "resume_session",
            payload: {
              sessionId: sessionIdRef.current,
              password: sessionPasswordRef.current,
              client: getClientFingerprint(),
            },
          });
        } else {
          // create new session with client fingerprint
          socketRef.current.send({
            service: "session",
            action: "new_session",
            payload: {
              client: getClientFingerprint(),
            },
          });
        }
      }
    } catch (err) {
      console.error("❌ connection failed:", err);
      setStatus("error");
      setError(err instanceof Error ? err.message : "Connection failed");
    }
  }, [handleIncomingMessage]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setStatus("idle");
  }, []);

  const sendMessage = useCallback(
    (service: string, action: string, payload: Record<string, any>) => {
      if (!socketRef.current || !socketRef.current.isReady()) {
        console.warn("socket not connected");
        return;
      }

      if (!sessionReady) {
        console.warn("session not authenticated");
        return;
      }

      try {
        socketRef.current.send({ service, action, payload });
      } catch (err) {
        console.error("failed to send message:", err);
        setError(err instanceof Error ? err.message : "Failed to send message");
      }
    },
    [sessionReady],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // notify server when no modals are active (only send once per transition)
  useEffect(() => {
    if (activeModals.size === 0 && !hasNotifiedNoModalsRef.current) {
      sendMessage("ui", "no_active_modals", {});
      hasNotifiedNoModalsRef.current = true;
    }
  }, [activeModals, sendMessage]);



  // auto-connect on mount
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

    // reset session: clear stored ids/password and attempt to create a new session
    const resetSession = useCallback(() => {
      console.log("resetting session");
      setSessionReady(false);
      setSessionId(null);
      setSessionPassword(null);
      setStatus("reconnecting");

      // disconnect existing socket to force fresh connection
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      // reconnect to get a fresh session
      connect().catch((err) => {
        console.error("failed to reconnect after reset:", err);
        setStatus("error");
      });
    }, [connect]);


  const value: AppContextType = {
    appEnableDebug,

    openModal,
    closeModal,
    getModalData,
    getInternetClientInstances,

    windowSmall,

    volume,
    setVolume,

    sessionId,
    sessionPassword,
    sessionReady,
    isSocketReady,
    lastAuthTime,

    // theme management
    theme: theme || "win95",
    setTheme,

    resetSession,

    // connection
    status,
    error,

    sendMessage,
    connect,
    disconnect,
    clearError,

    onMessage,
  };

  return (
    <AppContext.Provider value={value}>
      <MailProvider>
        <SecretProvider>
          <ChatProvider>
            <CTFProvider>
              <WebProvider>{children}</WebProvider>
            </CTFProvider>
          </ChatProvider>
        </SecretProvider>
      </MailProvider>
    </AppContext.Provider>
  );
}
